import type { FC } from "react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import type { ICommonServiceOutput, IResultStatus } from "@/Components/Api/CommonServiceOutput";
import { sendTelemetryEvent } from "@/lib/telemetry";
import TelemetryButton from "@Components/Button/TelemetryButton/TelemetryButton";
import type { IDropdownOptionV2 } from "@Components/Dropdown";
import { DropdownV2 } from "@Components/Dropdown";
import useAjax, { AjaxMethod } from "@Components/Hooks/useAjax";
import { Loading } from "@Components/Loading";
import { CommonEventType, MgmEventType, TelemetryEntity } from "@Components/Modules/Helpers";
import { TextboxAppearanceV2, TextboxV2 } from "@Components/Textbox";
import { Default } from "@Routes/MemberGetMemberLinks";

import * as css from "./MgmPhoneVerification.module.css";

const CountryCodeType = {
    PL: "+48",
    PT: "+351",
    UA: "+380",
};

export interface IMgmPhoneVerificationSendCode {
    receiverBonusAmount: number;
    description: string;
    placeholder: string;
    nextButtonText: string;
    customErrorMessage: string;
    onPhoneSubmissionResponse: (phoneNumber: string) => void;
}

type FieldNames = {
    phoneNumber: string;
    phoneNumberPrefix: string;
};

type SendConfirmationSmsResponseType = {
    commonServiceOutput: ICommonServiceOutput;
};

const MgmPhoneVerificationSendCode: FC<IMgmPhoneVerificationSendCode> = ({
    receiverBonusAmount,
    description,
    nextButtonText,
    placeholder,
    customErrorMessage,
    onPhoneSubmissionResponse,
}) => {
    const numberOptions: IDropdownOptionV2[] = [
        { label: CountryCodeType.PL, value: CountryCodeType.PL },
        { label: CountryCodeType.PT, value: CountryCodeType.PT },
        { label: CountryCodeType.UA, value: CountryCodeType.UA },
    ];

    useEffect(
        () =>
            sendTelemetryEvent({
                eventName: `${TelemetryEntity.Cashback}-${MgmEventType.PhoneVerificationPage}`,
                actionType: "View",
            }),
        [],
    );

    const {
        handleSubmit,
        register,
        setValue,
        formState: { errors },
        getValues,
        setError,
    } = useForm<FieldNames>({
        defaultValues: {
            phoneNumberPrefix: numberOptions[0].value as string,
        },
    });

    const { isLoading, request } = useAjax<SendConfirmationSmsResponseType>({
        ajaxAction: Default.url,
        ajaxMethod: AjaxMethod.Post,
        success: (result) => {
            const resultStatus = result?.commonServiceOutput?.resultStatus || ({} as IResultStatus);
            if (resultStatus.success) {
                onPhoneSubmissionResponse(getValues("phoneNumberPrefix") + getValues("phoneNumber"));
            } else {
                setError("phoneNumber", { type: "custom", message: resultStatus.errorMessage });
            }
        },
    });

    const onSubmit = (fields: FieldNames) => {
        request(
            { action: "SendConfirmationSms" },
            { phoneNumber: fields.phoneNumberPrefix + fields.phoneNumber },
        );
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <h2
                className={css.description}
                dangerouslySetInnerHTML={{
                    __html: description.replaceAll("{receiverBonusAmount}", receiverBonusAmount.toFixed(0)),
                }}
            />
            <div className={css.inputWrapper}>
                <DropdownV2
                    options={numberOptions}
                    onChange={(value) => setValue("phoneNumberPrefix", value.value as string)}
                />
                <TextboxV2
                    {...register("phoneNumber", {
                        required: customErrorMessage,
                        pattern: {
                            value: /^[1-9]{1}[0-9]{8}$/g,
                            message: customErrorMessage,
                        },
                    })}
                    type="tel"
                    placeholder={placeholder}
                    appearance={TextboxAppearanceV2.Round}
                    errorMessage={errors.phoneNumber?.message}
                />
            </div>
            <TelemetryButton
                appearance="magentaRoundAppearance"
                type="submit"
                telemetryCustom={{
                    eventName: `${TelemetryEntity.Common}-${CommonEventType.next}`,
                    actionType: "Click",
                }}
            >
                {nextButtonText}
            </TelemetryButton>
            {isLoading && <Loading isModal />}
        </form>
    );
};

export default MgmPhoneVerificationSendCode;
